import React from "react";
import styled from "styled-components/macro";
import Search from "@material-ui/icons/Search";
import SwapVert from "@material-ui/icons/SwapVert";
import DateRange from "@material-ui/icons/DateRange";
import { theme, getStatusMeta, STATUS_FILTER_GROUPS } from "./theme";
import {
  DATE_RANGE_PRESETS,
  CUSTOM_DATE_RANGE_KEY,
  getJobsLimitForDateRange,
  getPresetDateRange,
} from "./printerJobsQuery";

const LAYOUTS = [
  { key: "table", label: "Table" },
  { key: "grouped", label: "By unit" },
];

const JobListHeader = ({
  totalCount,
  // Search / status chips run over every loaded job (the whole fetched range);
  // say how many match so a filtered list reads as a complete result.
  matchingCount,
  isFiltered = false,
  statusCounts,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  sortAsc,
  onToggleSort,
  layout,
  onLayoutChange,
  // Per-printer history only (see printer_jobs.js): the room's name as the
  // title, preset chips + from/to inputs that drive a server refetch, and a
  // hint when the page ceiling is hit.
  title,
  dateRange = null,
  onDateRangeChange,
  pageLimitReached = false,
}) => {
  const showDateRange = !!dateRange && typeof onDateRangeChange === "function";
  return (
    <Wrapper>
      <TopRow>
        <TitleBlock>
          <Title>{title || "Print jobs"}</Title>
          <Subtitle>
            {totalCount} job{totalCount === 1 ? "" : "s"}
            {isFiltered ? ` · ${matchingCount} matching` : ""}
          </Subtitle>
        </TitleBlock>
        <LayoutSwitch>
          {LAYOUTS.map((l) => (
            <LayoutBtn
              key={l.key}
              isActive={layout === l.key}
              onClick={() => onLayoutChange(l.key)}
            >
              {l.label}
            </LayoutBtn>
          ))}
        </LayoutSwitch>
      </TopRow>

      <ControlsRow>
        <SearchBox>
          <Search style={{ fontSize: 17, color: theme.textFaint }} />
          <SearchInput
            placeholder="Search by file, user, or unit…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </SearchBox>

        <SortBtn onClick={onToggleSort} title="Toggle sort order">
          <SwapVert style={{ fontSize: 17 }} />
          {sortAsc ? "Oldest first" : "Newest first"}
        </SortBtn>
      </ControlsRow>

      <ChipsRow>
        <Chip isActive={!statusFilter} onClick={() => onStatusFilterChange(null)}>
          All ({totalCount})
        </Chip>
        {STATUS_FILTER_GROUPS.map((group) => {
          const count = group.statuses.reduce((sum, status) => sum + (statusCounts[status] || 0), 0);
          if (!count) return null;
          const meta = getStatusMeta(group.statuses[0]);
          return (
            <Chip
              key={group.key}
              isActive={statusFilter === group.key}
              chipColor={meta.color}
              chipBackground={meta.background}
              onClick={() => onStatusFilterChange(group.key)}
            >
              {group.label} ({count})
            </Chip>
          );
        })}
      </ChipsRow>

      {showDateRange ? (
        <DateRangeRow>
          <DateRangeLabel>
            <DateRange style={{ fontSize: 16 }} />
            History
          </DateRangeLabel>
          {DATE_RANGE_PRESETS.map((preset) => (
            <Chip
              key={preset.key}
              isActive={dateRange.presetKey === preset.key}
              onClick={() => onDateRangeChange(getPresetDateRange(preset.key))}
            >
              {preset.label}
            </Chip>
          ))}
          <DateInputs>
            <DateInput
              type="date"
              aria-label="From date"
              value={dateRange.fromDate}
              max={dateRange.toDate || undefined}
              onChange={(e) =>
                onDateRangeChange({
                  presetKey: CUSTOM_DATE_RANGE_KEY,
                  fromDate: e.target.value,
                  toDate: dateRange.toDate,
                })
              }
            />
            <DateInputsSeparator>to</DateInputsSeparator>
            <DateInput
              type="date"
              aria-label="To date"
              value={dateRange.toDate}
              min={dateRange.fromDate || undefined}
              onChange={(e) =>
                onDateRangeChange({
                  presetKey: CUSTOM_DATE_RANGE_KEY,
                  fromDate: dateRange.fromDate,
                  toDate: e.target.value,
                })
              }
            />
          </DateInputs>
          {pageLimitReached ? (
            <LimitHint>
              Showing the latest {getJobsLimitForDateRange(dateRange)} jobs in this range.
              Narrow the dates to see older ones.
            </LimitHint>
          ) : null}
        </DateRangeRow>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 24px 12px;
  font-family: ${theme.fontFamily};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 19px;
  font-weight: 600;
  color: ${theme.text};
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${theme.textFaint};
`;

const LayoutSwitch = styled.div`
  display: flex;
  padding: 3px;
  background: ${theme.background};
  border-radius: ${theme.radiusSm};
  gap: 2px;
`;

const LayoutBtn = styled.button`
  outline: none;
  border: none;
  cursor: pointer;
  font-family: ${theme.fontFamily};
  font-size: 12px;
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  padding: 6px 12px;
  border-radius: ${theme.radiusSm};
  color: ${({ isActive }) => (isActive ? theme.primaryHover : theme.textMuted)};
  background: ${({ isActive }) => (isActive ? theme.surface : "transparent")};
  box-shadow: ${({ isActive }) => (isActive ? theme.shadow : "none")};
  transition: all 150ms ease;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};

  &:focus-within {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primaryLight};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: ${theme.fontFamily};
  font-size: 13px;
  color: ${theme.text};
  background: transparent;

  &::placeholder {
    color: ${theme.textFaint};
  }
`;

const SortBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  font-family: ${theme.fontFamily};
  font-size: 13px;
  font-weight: 500;
  color: ${theme.textMuted};
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${theme.borderStrong};
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  outline: none;
  border: 1px solid ${({ isActive, chipColor }) => (isActive ? chipColor || theme.primary : theme.border)};
  cursor: pointer;
  font-family: ${theme.fontFamily};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: ${theme.radiusPill};
  color: ${({ isActive, chipColor }) => (isActive ? chipColor || theme.primaryHover : theme.textMuted)};
  background: ${({ isActive, chipBackground }) => (isActive ? chipBackground || theme.primaryLight : theme.surface)};
  transition: all 150ms ease;
`;

const DateRangeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const DateRangeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.textMuted};
  margin-right: 4px;
`;

const DateInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
`;

const DateInput = styled.input`
  padding: 5px 10px;
  font-family: ${theme.fontFamily};
  font-size: 12px;
  color: ${theme.text};
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusPill};
  outline: none;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primaryLight};
  }
`;

const DateInputsSeparator = styled.span`
  font-size: 12px;
  color: ${theme.textFaint};
`;

const LimitHint = styled.div`
  flex-basis: 100%;
  font-size: 12px;
  color: ${theme.warning};
`;

export { JobListHeader };
